import axios from 'axios';
import { message } from 'ant-design-vue';
import { BACKEND_API_URL } from '@/constants';

// Separate axios instance for org calls - withCredentials omitted so the browser
// never shows the native Basic Auth dialog on a 401 from Django's BasicAuthentication.
const orgAxios = axios.create({
  baseURL: BACKEND_API_URL,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Roles are scoped per organisation, so every panel request has to name the
// organisation it acts on. Reading it here - rather than threading it through
// ~40 call sites - keeps a single source of truth and survives pagination
// links, which are rebuilt without query params.
export const ORG_STORAGE_KEY = 'org_selected_organisation';

export const getSelectedOrganisationId = () =>
  localStorage.getItem(ORG_STORAGE_KEY) || '';

export const setSelectedOrganisationId = (uuid) => {
  if (uuid) {
    localStorage.setItem(ORG_STORAGE_KEY, uuid);
  } else {
    localStorage.removeItem(ORG_STORAGE_KEY);
  }
};

// Single source of truth for the org header, shared by every axios instance that
// talks to an org-scoped endpoint. Registered as an interceptor so GET/POST/
// PUT/PATCH/DELETE are covered without touching individual call sites. With no
// organisation selected the header is omitted, leaving the backend's
// single-organisation fallback intact.
export const attachOrganisationHeader = (config) => {
  const orgId = getSelectedOrganisationId();
  if (orgId && !config.headers['X-Organisation-Id']) {
    config.headers['X-Organisation-Id'] = orgId;
  }
  return config;
};

// Strip a malformed `Authorization` header before it leaves the browser so we
// never trigger DRF's "Authorization header must contain two space-delimited
// values" - happens when the org token is empty/expired-and-cleared.
orgAxios.interceptors.request.use((config) => {
  const auth = config.headers?.Authorization;
  if (typeof auth === 'string') {
    const token = auth.replace(/^Bearer\s*/i, '').trim();
    if (!token || token === 'null' || token === 'undefined') {
      delete config.headers.Authorization;
    }
  }

  return attachOrganisationHeader(config);
});

let orgSessionExpiredHandled = false;

const handleOrgSessionExpired = () => {
  if (orgSessionExpiredHandled) return;
  orgSessionExpiredHandled = true;
  localStorage.removeItem('org_access_token');
  localStorage.removeItem('org_refresh_token');
  message.error('Your session has expired. Please log in again.');
  // Use a hard redirect so any stale Pinia/component state is wiped.
  window.location.href = '/';
};

const isAuthExpiredError = (response) => {
  if (!response) return false;
  if (response.status !== 401) return false;
  const code = response.data?.code;
  return (
    code === 'token_not_valid' ||
    code === 'bad_authorization_header' ||
    code === 'user_not_found' ||
    code === 'authentication_failed' ||
    // No specific code - treat any 401 on an org request as session expiry.
    code === undefined
  );
};

orgAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    // Don't auto-logout on the login endpoint itself - the modal shows the
    // "wrong credentials" error inline.
    const isLoginCall = url.includes('/auth/organisation-login');
    if (!isLoginCall && isAuthExpiredError(error.response)) {
      handleOrgSessionExpired();
    }
    return Promise.reject(error);
  }
);

const orgPost = async (endpoint, data = {}) => {
  try {
    const response = await orgAxios.post(`/api${endpoint}`, data);
    return response;
  } catch (error) {
    return error.response;
  }
};

// Unauthenticated GET - the registration form runs before any token exists.
const orgPublicGet = async (endpoint, params = {}) => {
  try {
    const response = await orgAxios.get(`/api${endpoint}`, { params });
    return response;
  } catch (error) {
    return error.response;
  }
};

const orgGet = async (endpoint, token) => {
  try {
    const response = await orgAxios.get(`/api${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

const orgAuthPost = async (
  endpoint,
  data,
  token,
  headers = {},
  onUploadProgress
) => {
  try {
    const response = await orgAxios.post(`/api${endpoint}`, data, {
      headers: { Authorization: `Bearer ${token}`, ...headers },
      onUploadProgress,
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

const orgAuthPatch = async (endpoint, data, token) => {
  try {
    const response = await orgAxios.patch(`/api${endpoint}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

const orgAuthDelete = async (endpoint, token) => {
  try {
    const response = await orgAxios.delete(`/api${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

export const registerOrganisation = async (data) => {
  return await orgPost('/auth/organisation-register/', data);
};

export const checkOrganisationRegistration = async (params) => {
  return await orgPublicGet('/auth/organisation-registration-check/', params);
};

export const verifyEmail = async (data) => {
  return await orgPost('/auth/verify-email/', data);
};

export const resendVerification = async (data) => {
  return await orgPost('/auth/resend-verification/', data);
};

export const loginOrganisation = async (data) => {
  return await orgPost('/auth/organisation-login/', data);
};

export const fetchOrganisationUserInfo = async (token) => {
  return await orgGet('/user/user-info/', token);
};

// `scope=panel` (default) lists only orgs where the caller is admin/teacher.
export const fetchMyOrganisations = async (token, scope = 'panel') => {
  return await orgGet(`/organisations/my-organisations/?scope=${scope}`, token);
};

export const fetchOrganisationTeachers = async (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const endpoint = query
    ? `/organisations/teachers/?${query}`
    : '/organisations/teachers/';
  return await orgGet(endpoint, token);
};

export const fetchOrganisationAdmins = async (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const endpoint = query
    ? `/organisations/admins/?${query}`
    : '/organisations/admins/';
  return await orgGet(endpoint, token);
};

export const fetchOrganisationBoards = async (token) => {
  return await orgGet('/organisations/boards/', token);
};

export const fetchOrganisationSubjects = async (token, boardUuids = []) => {
  const query = boardUuids.map((u) => `boards=${u}`).join('&');
  const endpoint = query
    ? `/organisations/subjects/?${query}`
    : '/organisations/subjects/';
  return await orgGet(endpoint, token);
};

export const addOrganisationTeacher = async (token, data) =>
  await orgAuthPost('/organisations/teachers/', data, token);

export const editOrganisationTeacher = async (token, teacherUuid, data) =>
  await orgAuthPatch(`/organisations/teachers/${teacherUuid}/`, data, token);

export const deleteOrganisationTeacher = async (token, teacherUuid) =>
  await orgAuthDelete(`/organisations/teachers/${teacherUuid}/`, token);

export const addOrganisationAdmin = async (token, data) =>
  await orgAuthPost('/organisations/admins/', data, token);

export const editOrganisationAdmin = async (token, adminUuid, data) =>
  await orgAuthPatch(`/organisations/admins/${adminUuid}/`, data, token);

export const deleteOrganisationAdmin = async (token, adminUuid) =>
  await orgAuthDelete(`/organisations/admins/${adminUuid}/`, token);

export const fetchOrganisationStudents = async (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const endpoint = query
    ? `/organisations/students/?${query}`
    : '/organisations/students/';
  return await orgGet(endpoint, token);
};

export const addOrganisationStudent = async (token, data) =>
  await orgAuthPost('/organisations/students/', data, token);

export const editOrganisationStudent = async (token, studentUuid, data) =>
  await orgAuthPatch(`/organisations/students/${studentUuid}/`, data, token);

export const deleteOrganisationStudent = async (token, studentUuid) =>
  await orgAuthDelete(`/organisations/students/${studentUuid}/`, token);

export const fetchOrganisationBatches = async (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const endpoint = query
    ? `/organisations/batches/?${query}`
    : '/organisations/batches/';
  return await orgGet(endpoint, token);
};

export const addOrganisationBatch = async (token, data) =>
  await orgAuthPost('/organisations/batches/', data, token);

export const editOrganisationBatch = async (token, batchUuid, data) =>
  await orgAuthPatch(`/organisations/batches/${batchUuid}/`, data, token);

export const deleteOrganisationBatch = async (token, batchUuid) =>
  await orgAuthDelete(`/organisations/batches/${batchUuid}/`, token);

export const fetchOrganisationJoinRequests = async (
  token,
  statusFilter = 'PENDING'
) => {
  const query = statusFilter ? `?status=${statusFilter}` : '';
  return await orgGet(`/organisations/join-requests/${query}`, token);
};

export const actionOrganisationJoinRequest = async (
  token,
  requestUuid,
  action,
  declineReason = ''
) =>
  await orgAuthPost(
    `/organisations/join-requests/${requestUuid}/action/`,
    { action, decline_reason: declineReason },
    token
  );

export const fetchEligibleTutors = async (token, studentUuid, subjectUuid) => {
  return await orgGet(
    `/organisations/students/${studentUuid}/subjects/${subjectUuid}/eligible-tutors/`,
    token
  );
};

export const assignSubjectTutor = async (
  token,
  studentUuid,
  subjectUuid,
  tutorUuids
) =>
  await orgAuthPatch(
    `/organisations/students/${studentUuid}/subjects/${subjectUuid}/tutor/`,
    { tutor_uuids: tutorUuids },
    token
  );

export const fetchStudentInsights = async (token, studentUuid) => {
  return await orgGet(
    `/organisations/students/${studentUuid}/insights/`,
    token
  );
};

export const downloadStudentInsightsPdf = async (
  token,
  studentUuid,
  subjectUuid
) => {
  const qs = subjectUuid
    ? `?subject_uuid=${encodeURIComponent(subjectUuid)}`
    : '';
  try {
    return await orgAxios.get(
      `/api/organisations/students/${studentUuid}/insights/pdf/${qs}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      }
    );
  } catch (error) {
    return error.response;
  }
};

export const fetchParentEmailData = async (token, studentUuid) => {
  return await orgGet(
    `/organisations/students/${studentUuid}/parent-email/`,
    token
  );
};

export const sendParentReportEmail = async (token, studentUuid, payload) => {
  return await orgAuthPost(
    `/organisations/students/${studentUuid}/parent-email/send/`,
    payload,
    token
  );
};

export const fetchClassroomPerformance = async (token) => {
  return await orgGet('/organisations/classroom-performance/', token);
};

export const fetchClassroomSubjectInsights = async (token, boardName) => {
  return await orgGet(
    `/organisations/classroom-subject-insights/?board=${encodeURIComponent(boardName)}`,
    token
  );
};

// ─── Worksheet Builder · Add from Repository (org_access_token) ──────────────
export const fetchWorksheetFilterOptions = async (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const endpoint = query
    ? `/worksheets/repository/questions/filter-options/?${query}`
    : '/worksheets/repository/questions/filter-options/';
  return await orgGet(endpoint, token);
};

export const fetchRepositoryQuestions = async (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const endpoint = query
    ? `/worksheets/repository/questions/?${query}`
    : '/worksheets/repository/questions/';
  return await orgGet(endpoint, token);
};

export const fetchRepositoryQuestionDetail = async (token, uuid) => {
  return await orgGet(`/worksheets/repository/questions/${uuid}/`, token);
};

export const createWorksheet = async (token, data) =>
  await orgAuthPost('/worksheets/', data, token);

/**
 * GET /api/worksheets/title-available/?title=&exclude=
 * Live check that the teacher has no other worksheet with this name.
 * Returns { available, message }.
 */
export const checkWorksheetTitle = async (token, title, excludeUuid = null) => {
  const params = new URLSearchParams({ title });
  if (excludeUuid) params.set('exclude', excludeUuid);
  return await orgGet(`/worksheets/title-available/?${params}`, token);
};

export const fetchWorksheet = async (token, uuid) => {
  try {
    const response = await orgAxios.get(`/api/worksheets/${uuid}/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch worksheet details:', error);
    return null;
  }
};

export const createUploadedWorksheet = async (
  token,
  formData,
  onUploadProgress
) =>
  await orgAuthPost(
    '/worksheets/',
    formData,
    token,
    { 'Content-Type': 'multipart/form-data' },
    onUploadProgress
  );

export const fetchWorksheets = async (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const endpoint = query ? `/worksheets/?${query}` : '/worksheets/';
  return await orgGet(endpoint, token);
};

export const assignWorksheet = async (token, uuid, data) =>
  await orgAuthPost(`/worksheets/${uuid}/assign/`, data, token);

export const saveWorksheet = async (token, uuid, data = {}) =>
  await orgAuthPost(`/worksheets/${uuid}/save/`, data, token);

export const fetchWorksheetStudents = async (token, uuid) =>
  await orgGet(`/worksheets/${uuid}/students/`, token);

export const unassignWorksheet = async (token, uuid, data) =>
  await orgAuthPost(`/worksheets/${uuid}/unassign/`, data, token);

export const updateWorksheetDueDate = async (token, uuid, data) =>
  await orgAuthPost(`/worksheets/${uuid}/due-date/`, data, token);

export const deleteWorksheet = async (token, uuid) =>
  await orgAuthDelete(`/worksheets/${uuid}/`, token);

export const downloadWorksheetQuestionsPdf = async (token, payload) => {
  try {
    return await orgAxios.post('/api/worksheets/questions-pdf/', payload, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
  } catch (error) {
    return error.response;
  }
};

export const downloadSavedWorksheetPdfByUuid = async (token, uuid) => {
  try {
    return await orgAxios.get(`/api/worksheets/${uuid}/questions-pdf/`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
  } catch (error) {
    return error.response;
  }
};

// "Upload your own worksheet" draft: the paper as a PDF before it is saved.
export const downloadUploadedPaperPdf = async (token, formData) => {
  try {
    return await orgAxios.post(
      '/api/worksheets/uploaded-paper-pdf/',
      formData,
      {
        // orgAxios defaults to JSON, which makes axios serialise the FormData
        // to JSON and drop the file; multipart keeps it a real upload.
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      }
    );
  } catch (error) {
    return error.response;
  }
};

// ─── Notifications (org-admin context, org_access_token) ────────────────────
export const fetchOrgNotifications = async (token, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const endpoint = query ? `/notifications/?${query}` : '/notifications/';
  return await orgGet(endpoint, token);
};

export const fetchOrgNotificationUnreadCount = async (token) => {
  return await orgGet('/notifications/unread-count/', token);
};

export const markOrgNotificationRead = async (token, uuid) =>
  await orgAuthPost(`/notifications/${uuid}/read/`, {}, token);

export const markAllOrgNotificationsRead = async (token) =>
  await orgAuthPost('/notifications/mark-all-read/', {}, token);

export const deleteOrgNotification = async (token, uuid) =>
  await orgAuthDelete(`/notifications/${uuid}/`, token);

export const clearAllOrgNotifications = async (token) =>
  await orgAuthDelete('/notifications/clear-all/', token);

// ─── Invitations (org-admin context) ────────────────────────────────────────
export const sendStudentInvitation = async (token, payload) =>
  await orgAuthPost('/organisations/invitations/', payload, token);

// ─── Sorty AI Worksheet Generation ──────────────────────────────────────────

/**
 * POST /api/worksheets/generate/
 * Triggers Gemini AI worksheet generation as a background Celery task.
 * Returns 202 with { job_uuid, status }.
 */
export const triggerAiWorksheetGeneration = async (token, data) =>
  await orgAuthPost('/worksheets/generate/', data, token);

/**
 * GET /api/worksheets/generate/<jobUuid>/status/
 * Polls the status of a generation job.
 * Returns { status, worksheet_uuid?, error_message }.
 */
export const pollAiWorksheetGenerationStatus = async (token, jobUuid) => {
  return await orgGet(`/worksheets/generate/${jobUuid}/status/`, token);
};

/**
 * GET /api/worksheets/generate/in-progress/
 * Returns all PENDING/PROCESSING generation jobs for the current org.
 */
export const fetchInProgressGenerations = async (token) => {
  return await orgGet('/worksheets/generate/in-progress/', token);
};
